import { uniqueId } from 'lodash'
import { CreationToolSourceFile } from '../types'
import CreationToolError from '../types/error'
import CreationToolFileType from '../types/file-type'
import getSourceInfo, { getUrlMimeType } from './get-source-info'
import getVideoInfo from './get-video-info'

const checkIfVideoHasAudio = (video: any) =>
    !!video.mozHasAudio || !!video.webkitAudioDecodedByteCount || !!(video.audioTracks && video.audioTracks.length)

const validateVideo = (
    source: File | string,
    minDuration: number,
    maxDuration: number,
    requiresAudio: boolean
): Promise<CreationToolSourceFile> =>
    new Promise(async (resolve, reject) => {
        const { isVideo } = getSourceInfo(source)

        // if source is not a video, throw error
        if (!isVideo) {
            reject(CreationToolError.InvalidVideoFile)
            return
        }

        const mimeType = typeof source === 'string' ? getUrlMimeType(source) : null
        const preview = document.createElement('canvas')

        // youtube/vimeo urls
        if (typeof source === 'string' && (mimeType === 'youtube' || mimeType === 'vimeo')) {
            try {
                // fetch duration and privacy information for external video
                const { duration, title, width, height, isPrivate } = await getVideoInfo(source)

                preview.width = width
                preview.height = height

                if (isPrivate) {
                    // video is private
                    reject(CreationToolError.VideoPrivate)
                } else if (duration <= 0) {
                    // video is live stream
                    reject(CreationToolError.VideoLiveStream)
                } else if (duration > maxDuration) {
                    // video is too long
                    reject(CreationToolError.VideoTooLong)
                    // TODO: hook up ability to trim video from bulk
                    // } else if (duration > maxDuration) {
                    //     // video needs trimming
                    //     reject(CreationToolError.VideoNeedsTrim)
                } else if (duration < minDuration) {
                    // video is too short
                    reject(CreationToolError.VideoTooShort)
                } else {
                    resolve({
                        id: uniqueId(),
                        source,
                        frames: [],
                        width,
                        height,
                        duration,
                        type: CreationToolFileType.VIDEO_EMBED,
                        sourceUrl: source,
                        title,
                        preview,
                        hasAudio: true,
                        isTransparent: false,
                        isAnimated: duration > 0,
                        isUrl: true,
                    })
                }
            } catch (err) {
                reject(CreationToolError.InvalidVideoUrl)
            }

            return
        }

        // video file or url
        const video = document.createElement('video')

        video.onloadeddata = () => {
            if (video.duration > maxDuration) {
                // video is too long
                reject(CreationToolError.VideoTooLong)
                // TODO: hook up ability to trim video from bulk
                // } else if (video.duration > maxDuration) {
                //     // video needs trimming
                //     reject(CreationToolError.VideoNeedsTrim)
            } else if (video.duration < minDuration) {
                // video is too short
                reject(CreationToolError.VideoTooShort)
            } else if (requiresAudio && !checkIfVideoHasAudio(video)) {
                // video has no audio
                reject(CreationToolError.NoAudio)
            } else {
                preview.width = video.videoWidth || 1
                preview.height = video.videoHeight || 1
                preview.getContext('2d')?.drawImage(video, 0, 0)

                resolve({
                    id: uniqueId(),
                    source,
                    frames: [],
                    width: video.videoWidth || 1,
                    height: video.videoHeight || 1,
                    duration: video.duration,
                    type: CreationToolFileType.VIDEO,
                    sourceUrl: typeof source === 'string' ? source : undefined,
                    preview,
                    hasAudio: checkIfVideoHasAudio(video),
                    isTransparent: false,
                    isAnimated: video.duration > 0,
                    isUrl: typeof source === 'string',
                })
            }

            // revoke the object url we created
            if (typeof source !== 'string') {
                URL.revokeObjectURL(video.src)
            }
        }

        video.onerror = () => {
            reject(CreationToolError.InvalidVideoFile)

            // revoke the object url we created
            if (typeof source !== 'string') {
                URL.revokeObjectURL(video.src)
            }
        }

        video.preload = 'auto'
        video.src = typeof source === 'string' ? source : URL.createObjectURL(source)
    })

export default validateVideo
